import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, ShieldCheck, ArrowLeftRight, BarChart2,
  Wallet, IndianRupee, TrendingUp, Clock,
  TicketCheck, ArrowDownToLine, UserCheck, Settings,
  Activity, CheckCircle2, AlertCircle, XCircle,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { adminAPI } from '../../services/api';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Tooltip, Legend, Filler
);

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  purple: '#7C3AED', purpleSoft: '#EDE9FE',
  radius: 14, radiusSm: 8,
};

const fmtINR = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '₹0.00';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const baseOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
};

/* ── Status Badge ── */
const STATUS_STYLES = {
  success:   { bg: C.successSoft, color: C.success,  icon: CheckCircle2 },
  pending:   { bg: C.warnSoft,    color: C.warning,   icon: Clock },
  failed:    { bg: C.dangerSoft,  color: C.danger,    icon: XCircle },
  initiated: { bg: '#F1F5F9',     color: C.muted,     icon: Activity },
};
function StatusBadge({ status }) {
  const s = STATUS_STYLES[(status || '').toLowerCase()] || { bg: '#F1F5F9', color: C.muted };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color, textTransform: 'capitalize' }}>
      {status || 'unknown'}
    </span>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, icon: Icon, iconBg, iconColor, link, loading, sub }) {
  const [hov, setHov] = useState(false);
  const inner = (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.surface, border: `1px solid ${hov ? C.primaryTint : C.border}`,
        borderRadius: C.radius, padding: '20px',
        boxShadow: hov ? '0 8px 28px rgba(124,58,237,0.12)' : '0 1px 4px rgba(20,20,43,0.05)',
        transition: 'all .2s', cursor: link ? 'pointer' : 'default', height: '100%', minHeight: 110,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.muted, marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.ink, lineHeight: 1, letterSpacing: '-.03em', minHeight: 32, display: 'flex', alignItems: 'center' }}>
            {loading ? <span style={{ display: 'inline-block', width: 80, height: 28, background: C.borderSoft, borderRadius: 6, animation: 'pulse 1.5s infinite' }} /> : value}
          </div>
        </div>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} color={iconColor} strokeWidth={2} />
        </div>
      </div>
      {sub && <div style={{ fontSize: 12, color: C.muted }}>{sub}</div>}
      {link && <span style={{ fontSize: 12, fontWeight: 600, color: C.primary, marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>View details →</span>}
    </div>
  );
  return link
    ? <Link to={link} style={{ textDecoration: 'none', display: 'block' }}>{inner}</Link>
    : inner;
}

/* ── Chart Card ── */
function ChartCard({ title, subtitle, children, height = 230, action }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action && <span style={{ fontSize: 11, fontWeight: 600, color: C.primary, background: C.primarySoft, padding: '3px 10px', borderRadius: 20 }}>{action}</span>}
      </div>
      <div style={{ padding: '16px 18px', height }}>{children}</div>
    </div>
  );
}

/* ── Merchant Row ── */
function MerchantRow({ u }) {
  const [hov, setHov] = useState(false);
  const sc = {
    approved: { bg: C.successSoft, color: C.success },
    pending:  { bg: C.warnSoft,    color: C.warning },
    rejected: { bg: C.dangerSoft,  color: C.danger  },
  }[u.status] || { bg: '#F1F5F9', color: C.muted };
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderBottom: `1px solid ${C.borderSoft}`, background: hov ? C.primarySoft : 'transparent', transition: 'background .15s' }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: `linear-gradient(135deg,#7c3aed,#a78bfa)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
        {(u.username || 'M').slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.username}</div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'Menlo,monospace' }}>{u.mid || u.email}</div>
      </div>
      <span style={{ padding: '3px 10px', borderRadius: 999, background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', flexShrink: 0 }}>{u.status}</span>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, flexShrink: 0, minWidth: 80, textAlign: 'right' }}>{fmtINR(u.wallet_balance)}</div>
    </div>
  );
}

/* ── Txn Row ── */
function TxnRow({ txn }) {
  const [hov, setHov] = useState(false);
  return (
    <tr onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ borderBottom: `1px solid ${C.borderSoft}`, background: hov ? C.primarySoft : 'transparent', transition: 'background .15s' }}>
      <td style={{ padding: '11px 16px', color: C.primary, fontWeight: 600, fontFamily: 'Menlo,Consolas,monospace', fontSize: 11, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {txn.transaction_id || txn.id || '—'}
      </td>
      <td style={{ padding: '11px 16px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{txn.user?.username || '—'}</div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'Menlo,monospace' }}>{txn.user?.mid || ''}</div>
      </td>
      <td style={{ padding: '11px 16px', fontWeight: 800, color: C.ink, fontSize: 13, whiteSpace: 'nowrap' }}>{fmtINR(txn.amount)}</td>
      <td style={{ padding: '11px 16px' }}><StatusBadge status={txn.status} /></td>
    </tr>
  );
}

/* ══════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [d,       setD]       = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard()
      .then(res => setD(res.data?.data || {}))
      .catch(() => setD({}))
      .finally(() => setLoading(false));
  }, []);

  const stats   = d.statistics   || {};
  const recent  = d.recent       || {};
  const monthly = d.monthly_data || [];

  const usersStats   = stats.users        || {};
  const txnStats     = stats.transactions || {};
  const wdStats      = stats.withdrawals  || {};
  const ticketStats  = stats.tickets      || {};
  const revenueStats = stats.revenue      || {};

  const recentTxns  = recent.transactions || [];
  const recentUsers = recent.users        || [];

  /* ── KPIs ── */
  const kpis = [
    { label: 'Total Merchants',    value: usersStats.total ?? 0,             icon: Users,          iconBg: C.primarySoft, iconColor: C.primary, link: '/admin/merchants',               sub: `${usersStats.active ?? 0} active` },
    { label: 'Pending Approvals',  value: usersStats.pending_approvals ?? 0, icon: ShieldCheck,    iconBg: C.warnSoft,    iconColor: C.warning, link: '/admin/users?status=pending',    sub: 'Awaiting review' },
    { label: 'Total Transactions', value: txnStats.total ?? 0,               icon: ArrowLeftRight, iconBg: C.primarySoft, iconColor: C.primary, link: '/admin/transactions',            sub: `${txnStats.success ?? 0} succeeded` },
    { label: 'Open Tickets',       value: ticketStats.open ?? 0,             icon: TicketCheck,    iconBg: C.successSoft, iconColor: C.success, link: '/admin/support',                 sub: `${ticketStats.total ?? 0} total` },
  ];

  const moneyKpis = [
    { label: 'Total Revenue',       value: fmtINR(revenueStats.total_revenue),      icon: Wallet,          iconBg: '#ede9fe', iconColor: C.primary, sub: 'Platform earnings' },
    { label: 'Total Commissions',   value: fmtINR(revenueStats.total_commissions),  icon: IndianRupee,     iconBg: C.successSoft, iconColor: C.success, sub: 'Across all merchants' },
    { label: 'Total Withdrawals',   value: fmtINR(revenueStats.total_withdrawals),  icon: TrendingUp,      iconBg: C.purpleSoft,  iconColor: C.purple,  sub: 'Processed payouts' },
    { label: 'Pending Withdrawals', value: wdStats.pending ?? 0,                    icon: Clock,           iconBg: C.warnSoft,    iconColor: C.warning, sub: 'Needs action',              link: '/admin/withdrawals' },
  ];

  /* ── Shortcuts ── */
  const shortcuts = [
    { label: 'Merchants',     Icon: Users,           to: '/admin/merchants',     color: C.primary, bg: C.primarySoft },
    { label: 'Transactions',  Icon: ArrowLeftRight,  to: '/admin/transactions',  color: C.success, bg: C.successSoft },
    { label: 'Withdrawals',   Icon: ArrowDownToLine, to: '/admin/withdrawals',   color: C.warning, bg: C.warnSoft    },
    { label: 'Settlements',   Icon: BarChart2,       to: '/admin/settlements',   color: C.purple,  bg: C.purpleSoft  },
    { label: 'Users',         Icon: UserCheck,       to: '/admin/users',         color: C.danger,  bg: C.dangerSoft  },
    { label: 'Support',       Icon: TicketCheck,     to: '/admin/support',       color: '#06B6D4', bg: '#ECFEFF'     },
    { label: 'KYC',           Icon: ShieldCheck,     to: '/admin/kyc',           color: '#8B5CF6', bg: '#F5F3FF'     },
    { label: 'Settings',      Icon: Settings,        to: '/admin/site-settings', color: C.muted,   bg: '#F1F5F9'     },
  ];

  /* ── Chart data ── */
  const barMonthlyData = {
    labels: monthly.map(m => m.month),
    datasets: [
      { label: 'Deposits (₹)',    data: monthly.map(m => m.deposits),    backgroundColor: 'rgba(124,58,237,0.80)', borderRadius: 6, borderSkipped: false },
      { label: 'Withdrawals (₹)', data: monthly.map(m => m.withdrawals), backgroundColor: 'rgba(239,68,68,0.70)',  borderRadius: 6, borderSkipped: false },
    ],
  };
  const barMonthlyOpts = {
    ...baseOpts,
    plugins: { ...baseOpts.plugins, legend: { display: true, position: 'top', labels: { boxWidth: 10, font: { size: 11 } } } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { grid: { color: '#F1F5F9' }, ticks: { font: { size: 11 }, callback: v => '₹' + v } },
    },
  };

  const donutTxnData = {
    labels: ['Success', 'Pending', 'Failed'],
    datasets: [{
      data: [txnStats.success ?? 0, txnStats.pending ?? 0, Math.max(0, (txnStats.total ?? 0) - (txnStats.success ?? 0) - (txnStats.pending ?? 0))],
      backgroundColor: [C.success, C.warning, C.danger],
      borderWidth: 2, borderColor: C.surface, hoverOffset: 6,
    }],
  };
  const donutOpts = {
    ...baseOpts, cutout: '70%',
    plugins: { ...baseOpts.plugins, legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
  };

  const lineRevenueData = {
    labels: monthly.map(m => m.month),
    datasets: [{
      label: 'Revenue (₹)', data: monthly.map(m => m.deposits),
      borderColor: C.primary, backgroundColor: 'rgba(124,58,237,0.07)',
      borderWidth: 2.5, pointRadius: 4, pointBackgroundColor: C.primary, tension: 0.4, fill: true,
    }],
  };
  const lineOpts = {
    ...baseOpts,
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { grid: { color: '#F1F5F9' }, ticks: { font: { size: 11 }, callback: v => '₹' + v } },
    },
  };

  /* ── Stat pills for hero ── */
  const heroStats = [
    { label: 'Active Merchants', value: usersStats.active ?? 0,      color: '#a7f3d0', icon: '●' },
    { label: 'Today TXNs',       value: txnStats.today ?? 0,          color: '#c4b5fd', icon: '●' },
    { label: 'Pending KYC',      value: usersStats.pending_approvals ?? 0, color: '#fcd34d', icon: '●' },
  ];

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        .adm-shortcut-grid { display: grid; grid-template-columns: repeat(8,1fr); gap: 12px; margin-bottom: 24px; }
        .adm-kpi-grid       { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; margin-bottom: 18px; }
        .adm-charts-row1    { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 18px; }
        .adm-charts-row2    { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 24px; }
        .adm-recent-row     { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        @media (max-width: 1200px) {
          .adm-shortcut-grid { grid-template-columns: repeat(4,1fr); }
          .adm-kpi-grid      { grid-template-columns: repeat(2,1fr); }
        }
        @media (max-width: 860px) {
          .adm-shortcut-grid { grid-template-columns: repeat(4,1fr); }
          .adm-charts-row1   { grid-template-columns: 1fr; }
          .adm-charts-row2   { grid-template-columns: 1fr; }
          .adm-recent-row    { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .adm-shortcut-grid { grid-template-columns: repeat(2,1fr); }
          .adm-kpi-grid      { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(135deg,#7c3aed 0%,#5b21b6 100%)',
        borderRadius: C.radius, padding: '26px 28px', marginBottom: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, right: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 20, right: 200, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-.02em' }}>Admin Control Center</h2>
              <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.72)' }}>Real-time overview · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {heroStats.map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', borderRadius: 10, padding: '8px 14px', border: '1px solid rgba(255,255,255,0.15)' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginTop: 1 }}>{loading ? '—' : s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick Nav ── */}
      <div className="adm-shortcut-grid">
        {shortcuts.map(({ label, Icon, to, color, bg }) => (
          <button key={to} onClick={() => navigate(to)} style={{
            background: bg, border: `1.5px solid ${color}25`, borderRadius: C.radius,
            padding: '14px 10px', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            transition: 'transform .12s, box-shadow .12s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${color}30`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';   e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ width: 38, height: 38, borderRadius: 10, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${color}40` }}>
              <Icon size={17} color="#fff" strokeWidth={2.2} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 700, color, textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
          </button>
        ))}
      </div>

      {/* ── Count KPIs ── */}
      <div className="adm-kpi-grid">
        {kpis.map(kpi => <KpiCard key={kpi.label} {...kpi} loading={loading} />)}
      </div>

      {/* ── Money KPIs ── */}
      <div className="adm-kpi-grid">
        {moneyKpis.map(kpi => <KpiCard key={kpi.label} {...kpi} loading={loading} />)}
      </div>

      {/* ── Charts Row 1 ── */}
      <div className="adm-charts-row1">
        <ChartCard title="Monthly Volume" subtitle="Deposits vs Withdrawals (last 6 months)" height={240} action="Bar Chart">
          <Bar data={barMonthlyData} options={barMonthlyOpts} />
        </ChartCard>
        <ChartCard title="Revenue Trend" subtitle="Monthly deposit growth curve" height={240} action="Line Chart">
          <Line data={lineRevenueData} options={lineOpts} />
        </ChartCard>
      </div>

      {/* ── Charts Row 2 + Recent ── */}
      <div className="adm-charts-row2">
        <ChartCard title="Transaction Status" subtitle="Success · Pending · Failed breakdown" height={220} action="Doughnut">
          <Doughnut data={donutTxnData} options={donutOpts} />
        </ChartCard>

        {/* Platform Health card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.borderSoft}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Platform Health</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>Key metrics at a glance</div>
          </div>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Transaction Success Rate', val: txnStats.total ? Math.round(((txnStats.success || 0) / txnStats.total) * 100) : 0, color: C.success, icon: CheckCircle2 },
              { label: 'Merchant Approval Rate',   val: usersStats.total ? Math.round(((usersStats.active || 0) / usersStats.total) * 100) : 0, color: C.primary, icon: Users },
              { label: 'Support Resolution',       val: ticketStats.total ? Math.round((((ticketStats.total || 0) - (ticketStats.open || 0)) / ticketStats.total) * 100) : 0, color: C.warning, icon: TicketCheck },
            ].map(({ label, val, color, icon: Icon }) => (
              <div key={label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Icon size={13} color={color} strokeWidth={2.5} />
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: C.body }}>{label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color }}>{val}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: C.borderSoft, overflow: 'hidden' }}>
                  <div style={{ width: `${val}%`, height: '100%', borderRadius: 999, background: color, transition: 'width 1s ease' }} />
                </div>
              </div>
            ))}

            <div style={{ marginTop: 8, padding: '12px 16px', background: C.primarySoft, borderRadius: 10, border: `1px solid ${C.primaryTint}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Revenue Summary</div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {[
                  { label: 'Total',   value: fmtINR(revenueStats.total_revenue)     },
                  { label: 'Commish', value: fmtINR(revenueStats.total_commissions) },
                  { label: 'Payouts', value: fmtINR(revenueStats.total_withdrawals) },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, color: C.primary, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{loading ? '——' : value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent Transactions + Merchants ── */}
      <div className="adm-recent-row">

        {/* Transactions */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.borderSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Recent Transactions</span>
            <Link to="/admin/transactions" style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none', background: C.primarySoft, padding: '4px 12px', borderRadius: 20 }}>View All →</Link>
          </div>
          {recentTxns.length === 0 ? (
            <div style={{ padding: '36px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
              <div style={{ color: C.muted, fontSize: 13, fontWeight: 600 }}>No transactions yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg }}>
                    {['TXN ID', 'Merchant', 'Amount', 'Status'].map(col => (
                      <th key={col} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentTxns.map((txn, i) => <TxnRow key={txn.id || i} txn={txn} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Merchants */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.borderSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Recent Merchants</span>
            <Link to="/admin/merchants" style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none', background: C.primarySoft, padding: '4px 12px', borderRadius: 20 }}>View All →</Link>
          </div>
          {recentUsers.length === 0 ? (
            <div style={{ padding: '36px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>👤</div>
              <div style={{ color: C.muted, fontSize: 13, fontWeight: 600 }}>No merchants yet</div>
            </div>
          ) : recentUsers.map((u, i) => <MerchantRow key={u.id || i} u={u} />)}
        </div>

      </div>
    </div>
  );
}
